<script lang="ts">
  import { onMount, tick, untrack } from "svelte";
  import { api } from "../lib/api";
  import { createEditor, type EditorHandle } from "../lib/editor";
  import { buildPreviewHtml } from "../lib/preview";
  import ChatPanel from "./ChatPanel.svelte";
  import { runTool } from "../lib/tool-runner";
  import DiffView from "./DiffView.svelte";
  import { ensureSession } from "../lib/session";
  import type { PasteFile, VersionInfo } from "../lib/api";

  type Layout = "rows" | "columns" | "tree";

  /** Метаинформация паста, которая живёт прямо в дереве файлов. */
  interface PasteMeta {
    layout?: Layout;
    /** Доля редактора в .panes, 0..1. */
    split?: number;
    /** Что показываем слева: код или чат. */
    mode?: "code" | "ai";
    /** Устаревшее поле (панель была отдельным пейном). */
    ai?: boolean;
    active?: string;
    /** Любая другая метаинформация сохраняется как есть. */
    [key: string]: unknown;
  }

  const META_PATH = ".vetchka";
  const DEFAULT_SPLIT = 0.5;
  const MIN_SPLIT = 0.15;
  const MAX_SPLIT = 0.85;

  interface Props {
    slug: string | null;
    initialTitle?: string;
    initialFiles?: PasteFile[];
    isOwner?: boolean;
    onCreated?: (slug: string) => void;
  }

  const DEFAULT_FILES: PasteFile[] = [
    {
      path: "index.html",
      content:
        "<!doctype html>\n<html>\n<head>\n  <meta charset=\"utf-8\" />\n  <link rel=\"stylesheet\" href=\"style.css\" />\n</head>\n<body>\n  <h1>Hello, vetchka</h1>\n  <p id=\"out\"></p>\n  <script src=\"main.js\"><\/script>\n</body>\n</html>\n",
    },
    {
      path: "style.css",
      content: "body { font-family: system-ui; padding: 24px; }\nh1 { color: #4ec9b0; }\n",
    },
    {
      path: "main.js",
      content:
        "const out = document.getElementById('out');\nout.textContent = 'edit me: ' + new Date().toISOString();\n",
    },
    { path: META_PATH, content: '{\n  "layout": "columns",\n  "split": 0.5\n}\n' },
  ];

  let {
    slug,
    initialTitle = "",
    initialFiles = DEFAULT_FILES,
    isOwner = true,
    onCreated,
  }: Props = $props();

  // ---- Метаинформация ----------------------------------------------------
  function readMeta(files: PasteFile[]): PasteMeta {
    const file = files.find((f) => f.path === META_PATH);
    if (!file) return {};
    try {
      const parsed: unknown = JSON.parse(file.content);
      if (!parsed || typeof parsed !== "object") return {};
      return parsed as PasteMeta;
    } catch {
      return {};
    }
  }

  function normalizeLayout(v: unknown): Layout | undefined {
    return v === "rows" || v === "columns" || v === "tree" ? v : undefined;
  }

  function normalizeSplit(v: unknown): number | undefined {
    return typeof v === "number" && Number.isFinite(v)
      ? Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, v))
      : undefined;
  }

  // Сознательно берём только начальные значения: дальше состоянием владеем мы.
  const boot = untrack(() => {
    const files = initialFiles.map((f) => ({ ...f }));
    const meta = readMeta(files);
    const active =
      meta.active && files.some((f) => f.path === meta.active)
        ? meta.active
        : (files.find((f) => f.path !== META_PATH)?.path ?? files[0]?.path ?? "index.html");
    return {
      title: initialTitle,
      files,
      activePath: active,
      layout: normalizeLayout(meta.layout) ?? "columns",
      split: normalizeSplit(meta.split) ?? DEFAULT_SPLIT,
      // Режим чата хранится в пасте, но доступен только владельцу:
      // гость не должен открывать паст сразу в ai-панели (она вернёт 401).
      mode: (isOwner && (meta.mode === "ai" || meta.ai === true) ? "ai" : "code") as
        | "code"
        | "ai",
    };
  });

  // Slug — внутреннее состояние: после автосоздания первой версии редактор
  // не пересоздаётся, поэтому URL и все дальнейшие сохранения идут по нему.
  const initialSlug = untrack(() => slug);
  let currentSlug = $state(initialSlug);
  let title = $state(boot.title);
  let files = $state<PasteFile[]>(boot.files);
  let activePath = $state(boot.activePath);
  let layout = $state<Layout>(boot.layout);
  let split = $state(boot.split);
  let mode = $state<"code" | "ai">(boot.mode);

  let editorHost = $state<HTMLDivElement | undefined>();
  let editor: EditorHandle | null = null;
  let dirty = $state(false);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let status = $state("");

  // История версий
  let historyOpen = $state(false);
  let versions = $state<VersionInfo[]>([]);
  let versionsLoading = $state(false);
  let versionBusy = $state(false);

  // Дифф версии с текущим состоянием.
  let diff = $state<{ title: string; label: string; before: PasteFile[] } | null>(null);

  // Форк
  let forkBusy = $state(false);
  let forkProgress = $state<{ attempts: number; elapsedMs: number } | null>(null);

  // Заголовок: если он не задан, в поле показываем путь паста.
  const titlePlaceholder = $derived(currentSlug ? `/p/${currentSlug}` : "/new");
  const splitPercent = $derived((split * 100).toFixed(2));

  // ---- Запись метаинформации --------------------------------------------
  /**
   * Обновляет .vetchka в дереве файлов. Именно этот файл — источник истины
   * для layout'а, размеров панелей и активного файла (а не localStorage).
   */
  function writeMeta(patch: Partial<PasteMeta>) {
    // Если пользователь прямо сейчас правит .vetchka — не затираем его
    // структурными изменениями (JSON ещё может быть недописан).
    const structural =
      patch.layout !== undefined ||
      patch.split !== undefined ||
      patch.mode !== undefined;
    if (structural && activePath === META_PATH && editor) return;

    const next: PasteMeta = { ...readMeta(files), ...patch };
    const body = `${JSON.stringify(next, null, 2)}\n`;
    const idx = files.findIndex((f) => f.path === META_PATH);

    if (idx === -1) {
      files = [...files, { path: META_PATH, content: body }];
    } else if (files[idx]?.content !== body) {
      files = files.map((f, i) => (i === idx ? { ...f, content: body } : f));
    } else {
      return;
    }

    dirty = true;
    // Мета (.vetchka) попадает в файлы, но сама по себе паст не создаёт:
    // просто переключить layout — ещё не повод заводить запись в БД.
    if (currentSlug) scheduleAutosave();
  }

  function mountEditor() {
    // В режиме чата узла редактора нет — монтировать некуда.
    if (!editorHost || mode !== "code") return;
    editor?.destroy();
    const f = files.find((x) => x.path === activePath) ?? files[0];
    if (!f) return;
    editor = createEditor({
      parent: editorHost,
      path: f.path,
      value: f.content,
      onChange: (v) => {
        const file = files.find((x) => x.path === activePath);
        if (!file) return;
        file.content = v;
        dirty = true;
        scheduleAutosave();
        schedulePreview();
      },
      onSave: () => void save(false),
    });
    activePath = f.path;
  }

  let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleAutosave() {
    if (autosaveTimer) clearTimeout(autosaveTimer);
    if (!isOwner) return; // гость не сохраняет
    // Если паста ещё нет в БД, первое изменение создаст его автоматически.
    autosaveTimer = setTimeout(() => void save(true), 1500);
  }

  async function save(silent: boolean) {
    if (!isOwner) {
      if (!silent) error = "паст доступен только для чтения";
      return;
    }
    if (saving) return;
    saving = true;
    error = null;
    if (!silent) status = currentSlug ? "saving…" : "creating…";
    try {
      if (!currentSlug) {
        const res = await api.createPaste(title, files);
        currentSlug = res.slug;
        dirty = false;
        status = "created";
        onCreated?.(res.slug);
      } else if (dirty) {
        await api.updatePaste(currentSlug, { title, files });
        dirty = false;
        status = silent ? `saved ${new Date().toLocaleTimeString()}` : "saved";
        // Адрес превью изменился (/draft → /p/<slug>) — перерисовываем.
        schedulePreview();
      } else if (!silent) {
        await api.updatePaste(currentSlug, { title });
        status = "saved";
      }
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      status = "";
    } finally {
      saving = false;
    }
    // dirty и slug определяют адрес превью: сохранение возвращает паст
    // на настоящий URL вместо черновика.
    schedulePreview();
  }

  // ---- Файлы -------------------------------------------------------------
  function addFile() {
    const name = prompt("file name (e.g. script.js)");
    if (!name) return;
    if (files.some((f) => f.path === name)) {
      alert("file already exists");
      return;
    }
    files = [...files, { path: name, content: "" }];
    selectFile(name);
    touch();
  }

  function removeFile(path: string) {
    if (path === META_PATH) {
      alert(".vetchka хранит layout паста и удалению не подлежит");
      return;
    }
    if (files.length <= 1) return;
    if (!confirm(`delete ${path}?`)) return;
    files = files.filter((f) => f.path !== path);
    if (activePath === path) selectFile(files[0]!.path);
    touch();
  }

  function renameFile(path: string) {
    const next = prompt("new name", path);
    if (!next || next === path) return;
    if (files.some((f) => f.path === next)) {
      alert("file already exists");
      return;
    }
    const f = files.find((x) => x.path === path);
    if (!f) return;
    f.path = next;
    activePath = next;
    mountEditor();
    touch();
  }

  /** Правка структуры файлов (не через редактор). */
  function touch() {
    dirty = true;
    scheduleAutosave();
    schedulePreview();
  }

  /** Переключает активный файл и пересоздаёт редактор. */
  function selectFile(path: string) {
    if (path === activePath) return;
    activePath = path;
    mountEditor();
    writeMeta({ active: path });
  }

  // ---- Layout и ресайзер -------------------------------------------------
  function setLayout(next: Layout) {
    layout = next;
    writeMeta({ layout: next });
  }

  let dragKind = $state<"main" | null>(null);
  let panesEl: HTMLDivElement;

  const dragCursor = $derived(
    dragKind === null ? "" : layout === "rows" ? "row-resize" : "col-resize",
  );

  /**
   * Тянем полосу между панелями.
   *
   * На время перетаскивания поверх окна кладём прозрачный «щит»: иначе
   * курсор, попав на iframe превью, перестаёт слать события родительскому
   * документу и перетаскивание залипает.
   */
  function beginResize(kind: "main", e: PointerEvent) {
    dragKind = kind;
    document.body.style.userSelect = "none";
    e.preventDefault();

    const move = (ev: PointerEvent) => {
      if (!panesEl) return;
      const rect = panesEl.getBoundingClientRect();
      const ratio =
        layout === "rows"
          ? (ev.clientY - rect.top) / rect.height
          : (ev.clientX - rect.left) / rect.width;
      split = Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, ratio));
    };

    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      document.body.style.userSelect = "";
      dragKind = null;
      writeMeta({ split: Math.round(split * 1000) / 1000 });
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /**
   * Переключает левую половину между кодом и чатом.
   * Редактор уничтожаем при уходе из режима кода и монтируем заново при
   * возврате — DOM-узел под CodeMirror пересоздаётся.
   */
  async function setMode(next: "code" | "ai") {
    if (!isOwner || next === mode) return;
    if (next === "ai") {
      // Чату нужен paste id: молча создаём первую версию, не блокируя панель.
      if (!currentSlug) await save(false);
      if (!currentSlug) return; // создать не удалось — остаёмся в коде
      editor?.destroy();
      editor = null;
    }
    mode = next;
    writeMeta({ mode: next });
    if (next === "code") {
      await tick();
      mountEditor();
      void flushPreview();
    }
  }

  /** Применяет метаинформацию из .vetchka (после отката версии). */
  function applyMeta(nextFiles: PasteFile[]) {
    const meta = readMeta(nextFiles);
    layout = normalizeLayout(meta.layout) ?? layout;
    split = normalizeSplit(meta.split) ?? split;
    if (isOwner) {
      mode =
        meta.mode === "ai" || meta.ai === true
          ? "ai"
          : meta.mode === "code"
            ? "code"
            : mode;
    }
  }

  // ---- Превью ------------------------------------------------------------
  const ucOrigin =
    import.meta.env.VITE_UC_ORIGIN ??
    `${location.protocol}//uc.${location.hostname.replace(/^www\./, "")}${location.port ? ":" + location.port : ""}`;
  /** Пока черновик нужен принудительно (например, для execute_js). */
  let forceDraft = $state(false);
  /** Что сейчас реально отрисовано в iframe (dirty может ещё не дойти). */
  let previewMode = $state<"none" | "url" | "draft">("none");

  /**
   * Get-параметры для превью-демки (?zoom=…&lat=…). В превью по URL они
   * попадают в location.search настоящего документа, поэтому паст может
   * читать их как обычно. Вставлять можно и просто ссылку целиком.
   */
  let queryRaw = $state(initialQuery());
  /** Поле в фокусе — не перетираем ввод значениями из превью. */
  let queryFocused = $state(false);
  const query = $derived(normalizeQuery(queryRaw));

  let previewFrame: HTMLIFrameElement | undefined = $state();
  let previewTimer: ReturnType<typeof setTimeout> | null = null;
  /** Есть отложенный рендер, ещё не отправленный в песочницу. */
  let previewPending = false;

  /** Отправляет текущее содержимое в sandbox-iframe на UC-origin. */
  // Токен привязывает ответы execute_js к нашему окну.
  const evalToken = crypto.randomUUID();
  let renderSeq = 0;
  let currentRenderId = "";
  let readyRenderId = "";

  /** Токен черновика, который сейчас опубликован (переиспользуем его). */
  let draftToken: string | null = null;
  /** Цепочка публикаций: рендеры не должны обгонять друг друга. */
  let publishTask: Promise<void> = Promise.resolve();
  /** URL, который сейчас показан в iframe (для демо-окна). */
  let currentUrl = "";

  const plainUrl = $derived(
    currentSlug
      ? `${ucOrigin}/p/${encodeURIComponent(currentSlug)}/preview.html${query ? `?${query}` : ""}`
      : "",
  );

  /**
   * Нужен ли черновик: пока есть несохранённые правки (или паста ещё нет),
   * живое содержимое отдаётся сервером по настоящему URL.
   */
  const needDraft = $derived(forceDraft || dirty || !currentSlug);

  function buildUrl(token: string | null, version: string): string {
    const params = new URLSearchParams(query);
    const base = currentSlug
      ? `${ucOrigin}/p/${encodeURIComponent(currentSlug)}/index.html`
      : `${ucOrigin}/d/${token}/preview.html`;
    if (currentSlug && token) params.set("d", token);
    params.set("__v", version);
    return `${base}?${params.toString()}`;
  }

  /**
   * Публикует текущее содержимое и переводит превью на его URL.
   *
   * В БД ничего не пишется: черновик живёт в памяти сервера и принадлежит
   * сессии. Так у iframe всегда настоящий адрес (location, History API,
   * относительные пути), а не about:srcdoc.
   */
  function flushPreview(): Promise<void> {
    if (previewTimer) clearTimeout(previewTimer);
    previewPending = false;
    const renderId = String(++renderSeq);
    currentRenderId = renderId;

    publishTask = publishTask.then(async () => {
      if (renderId !== currentRenderId) return; // устарел, его обогнали

      if (!needDraft) {
        // Сохранённое состояние отдаёт сервер: черновик не нужен.
        previewMode = "url";
        const url = plainUrl;
        if (url) showUrl(url);
        return;
      }

      const html = buildPreviewHtml(files, { evalToken, renderId });
      try {
        const res = await api.putDraft(html, draftToken);
        if (renderId !== currentRenderId) return;
        draftToken = res.token;
        previewMode = "draft";
        showUrl(buildUrl(res.token, renderId));
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
      }
    });
    return publishTask;
  }

  function showUrl(url: string) {
    currentUrl = url;
    previewFrame?.contentWindow?.postMessage({ type: "vetchka:url", url }, "*");
    pushToDemo();
  }

  /**
   * Мост для execute_js приезжает вместе с черновиком. Если превью сейчас
   * на сохранённой странице — публикуем черновик и ждём готовности.
   */
  async function ensureEvalContext(): Promise<boolean> {
    if (previewMode === "draft") return true;
    forceDraft = true;
    await flushPreview();
    await waitPreviewReady(2000);
    return readyRenderId === currentRenderId;
  }

  function schedulePreview() {
    if (previewTimer) clearTimeout(previewTimer);
    previewPending = true;
    previewTimer = setTimeout(() => {
      void flushPreview();
    }, 400);
  }


  // Окно демо: живёт отдельно и подхватывает правки, пока открыто.
  let demoWin: Window | null = null;
  let demoToken = "";

  /**
   * Отправляет содержимое в открытое демо-окно.
   * Используется, когда демо показывает не серверный рендер, а inline-HTML
   * (новый паст или несохранённые правки).
   */
  function pushToDemo() {
    if (!demoWin || demoWin.closed) {
      demoWin = null;
      return;
    }
    demoWin.postMessage({ type: "vetchka:url", url: currentUrl }, "*");
  }

  /**
   * Открывает превью в отдельном окне без интерфейса vetchka.
   *
   * Сохранённый паст без правок грузится сервером по slug — так корректно
   * работают относительные пути к файлам. Если есть несохранённые изменения
   * или паст ещё не создан, содержимое передаётся через postMessage.
   */
  function openDemo() {
    // Демо открываем синхронно (иначе блокировщик popup'ов), но содержимое
    // то же, что в превью: настоящий URL, а не inline-HTML.
    const params = new URLSearchParams();
    if (currentSlug) params.set("slug", currentSlug);
    for (const [k, v] of new URLSearchParams(query)) {
      if (k !== "slug" && k !== "draft" && k !== "__v") params.append(k, v);
    }
    const target = draftToken ? buildUrl(draftToken, String(renderSeq)) : plainUrl;
    if (!target) {
      error = "нечего показывать";
      return;
    }
    const path = target.startsWith(ucOrigin) ? target.slice(ucOrigin.length) : target;
    const url = `${ucOrigin}/demo?${params.toString()}&src=${encodeURIComponent(path)}`;
    const win = window.open(url, "_blank");
    if (!win) {
      error = "popup blocked — разрешите всплывающие окна";
      return;
    }
    demoWin = win;
  }

  // ---- История версий ----------------------------------------------------
  async function loadVersions() {
    if (!currentSlug) return;
    versionsLoading = true;
    try {
      const res = await api.listVersions(currentSlug);
      versions = res.versions;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      versionsLoading = false;
    }
  }

  async function toggleHistory() {
    historyOpen = !historyOpen;
    if (historyOpen) await loadVersions();
  }

  /**
   * Единственное действие «зафиксировать»: сначала сохраняет head,
   * затем создаёт версию. Живёт в панели истории — там, где версии и нужны.
   */
  async function snapshotNow() {
    if (!currentSlug || versionBusy) return;
    versionBusy = true;
    error = null;
    try {
      if (dirty || saving) {
        await api.updatePaste(currentSlug, { title, files });
        dirty = false;
      }
      await api.saveVersion(currentSlug);
      status = `version saved ${new Date().toLocaleTimeString()}`;
      await loadVersions();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      versionBusy = false;
    }
  }

  async function restore(v: VersionInfo) {
    if (!currentSlug || versionBusy) return;
    if (dirty && !confirm("есть несохранённые правки — они будут перезаписаны. продолжить?")) return;
    if (!confirm(`откатить к версии от ${formatDate(v.createdAt)}?`)) return;
    versionBusy = true;
    error = null;
    try {
      await api.restoreVersion(currentSlug, v.id);
      const fresh = await api.getPaste(currentSlug);
      files = fresh.files.map((f) => ({ ...f }));
      applyMeta(files);
      if (!files.some((f) => f.path === activePath)) {
        activePath = files[0]?.path ?? "index.html";
      }
      dirty = false;
      mountEditor();
      status = "restored";
      void flushPreview();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      versionBusy = false;
    }
  }

  function fileCountLabel(n: number): string {
    return `${n} ${n === 1 ? "file" : "files"}`;
  }

  function formatDate(ts: number): string {
    const d = new Date(ts);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  }

  async function showDiff(v: VersionInfo) {
    if (!currentSlug) return;
    try {
      const res = await api.versionFiles(currentSlug, v.id);
      diff = {
        title: formatDate(v.createdAt),
        label: `версия ${formatDate(v.createdAt)}`,
        before: res.files,
      };
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  /**
   * Форк: создаёт копию паста во владение текущей сессии.
   * Гостю сначала нужна сессия — решаем PoW прямо здесь.
   */
  async function fork() {
    if (!currentSlug || forkBusy) return;
    forkBusy = true;
    error = null;
    forkProgress = null;
    try {
      status = "forking…";
      await ensureSession((pr) => (forkProgress = pr));
      if (dirty) {
        // Сохраняем свои правки в исходный паст только если мы владелец.
        if (isOwner) {
          await api.updatePaste(currentSlug, { title, files });
          dirty = false;
        }
      }
      const res = await api.forkPaste(currentSlug, `${title.trim() || "untitled"} (fork)`);
      status = "forked";
      location.href = `/p/${res.slug}`;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      status = "";
    } finally {
      forkBusy = false;
      forkProgress = null;
    }
  }

  /**
   * Параметры из адреса страницы: и search, и hash (код паста умеет оба,
   * поэтому ссылка вида /p/<slug>?zoom=2#lng=1 должна доехать целиком).
   */
  function initialQuery(): string {
    try {
      const params = new URLSearchParams(location.search);
      for (const [k, v] of new URLSearchParams(location.hash.replace(/^#/, ""))) {
        params.set(k, v);
      }
      return params.toString();
    } catch {
      return "";
    }
  }

  /**
   * Паст поменял свой URL (например, сохранил viewport карты). Показываем это
   * в адресной строке приложения, чтобы ссылкой можно было поделиться, и
   * запоминаем в поле параметров — следующая отрисовка учтёт их.
   */
  let mirrorTimer: ReturnType<typeof setTimeout> | null = null;
  function mirrorLocation(href: string) {
    if (queryFocused || !currentSlug) return;
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return;
    }
    if (!url.pathname.startsWith(`/p/${currentSlug}/`) && !url.pathname.startsWith("/d/")) return;
    const search = url.search.replace(/^\?/, "");
    if (search === query) return;
    if (mirrorTimer) clearTimeout(mirrorTimer);
    mirrorTimer = setTimeout(() => {
      queryRaw = search;
      history.replaceState(null, "", `/p/${currentSlug}${url.search}${url.hash}`);
    }, 250);
  }

  /**
   * Приводит ввод поля к query-строке: принимает и `zoom=2&lat=3`, и ссылку
   * целиком (`https://host/demo?slug=…&zoom=2`), и `?zoom=2`.
   */
  function normalizeQuery(raw: string): string {
    let value = raw.trim();
    if (!value) return "";
    value = value.split("#")[0] ?? value;
    const q = value.indexOf("?");
    if (q >= 0) value = value.slice(q + 1);
    const params = new URLSearchParams(value.replace(/^[?&]+/, ""));
    // slug/token — служебные для demo-страницы, в паст им нечего попадать.
    params.delete("slug");
    params.delete("token");
    return params.toString();
  }

  /**
   * Поле размыли/нажали Enter — фиксируем нормализованный вид, чтобы в поле
   * осталась чистая query-строка, а не вся ссылка.
   */
  /**
   * Нормализует текст поля после ввода/потери фокуса. Перерисовку не
   * запускает: рендер уже случился на oninput с тем же нормализованным
   * значением, а лишний flush — это лишняя перезагрузка превью.
   */
  function commitQuery() {
    if (queryRaw !== query) queryRaw = query;
  }

  function onQueryInput() {
    // Превью по URL возможно только для сохранённого паста: если его ещё нет,
    // создаём первую версию молча — как при открытии чата.
    if (query && !currentSlug) void save(false);
    schedulePreview();
  }

  // ---- Инструменты модели -------------------------------------------------
  async function waitPreviewReady(timeoutMs = 1500): Promise<void> {
    const target = currentRenderId;
    const started = Date.now();
    while (readyRenderId !== target && Date.now() - started < timeoutMs) {
      await new Promise((r) => setTimeout(r, 40));
    }
  }

  /**
   * Выполняет код в превью-iframe (uc-origin, sandbox без allow-same-origin).
   * Код — тело async-функции: доступны await и return.
   */
  /**
   * Общий вызов песочницы: отправляет запрос во вложенный iframe
   * и ждёт ответ с тем же id.
   */
  function previewCall(
    kind: "eval" | "console",
    payload: Record<string, unknown>,
    timeoutMs = 5000,
  ): Promise<Record<string, unknown> | null> {
    const requestType = kind === "eval" ? "vetchka:eval" : "vetchka:console-get";
    const resultType = kind === "eval" ? "vetchka:eval-result" : "vetchka:console-result";

    return new Promise((resolve) => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => {
        window.removeEventListener("message", onMessage);
        resolve(null);
      }, timeoutMs);

      function onMessage(event: MessageEvent) {
        const data = event.data as (Record<string, unknown> & { type?: string; id?: string }) | null;
        if (!data || data.type !== resultType || data.id !== id) return;
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        resolve(data);
      }

      window.addEventListener("message", onMessage);
      flushPreview();
      void waitPreviewReady().then(() => {
        previewFrame?.contentWindow?.postMessage(
          { type: requestType, token: evalToken, id, ...payload },
          "*",
        );
      });
    });
  }

  /**
   * Выполняет код в превью-iframe (uc-origin, sandbox без allow-same-origin).
   * Код — тело async-функции: доступны await и return.
   */
  async function executeJs(code: string): Promise<{ ok: boolean; value?: string; error?: string }> {
    await ensureEvalContext();
    const data = await previewCall("eval", { code });
    if (forceDraft) {
      forceDraft = false;
      schedulePreview();
    }
    if (!data) return { ok: false, error: "timeout: код не ответил за 5 с" };
    return {
      ok: data.ok === true,
      value: typeof data.value === "string" ? data.value : undefined,
      error: typeof data.error === "string" ? data.error : undefined,
    };
  }

  /** Последние ошибки/предупреждения консоли из превью. */
  async function getConsoleErrors(limit: number): Promise<Array<{ level: string; text: string }>> {
    await ensureEvalContext();
    const data = await previewCall("console", { limit });
    const entries = data?.entries;
    if (!Array.isArray(entries)) return [];
    return entries
      .filter((e): e is { level?: unknown; text?: unknown } => typeof e === "object" && e !== null)
      .map((e) => ({ level: String(e.level ?? "error"), text: String(e.text ?? "") }));
  }

  /** Инструменты модели: файлы + выполнение кода в превью. */
  async function executeTool(name: string, args: Record<string, unknown>): Promise<string> {
    return runTool(name, args, {
      getFiles: () => files,
      setFiles: (next) => {
        files = next;
        if (!files.some((f) => f.path === activePath)) {
          activePath = files[0]?.path ?? "index.html";
        }
      },
      getActivePath: () => activePath,
      onChanged: () => {
        // В режиме чата узла редактора нет — достаточно перерисовать превью.
        if (mode === "code") mountEditor();
        touch();
      },
      executeJs,
      getConsoleErrors,
      getTitle: () => title,
      setTitle: (next) => {
        title = next;
        dirty = true;
        scheduleAutosave();
      },
    });
  }

  onMount(() => {
    const onReady = (event: MessageEvent) => {
      const data = event.data as { type?: string; token?: string; renderId?: string } | null;
      if (!data || data.type !== "vetchka:eval-ready" || data.token !== evalToken) return;
      readyRenderId = data.renderId ?? "";
    };
    const onLocation = (event: MessageEvent) => {
      if (event.origin !== ucOrigin) return;
      const data = event.data as { type?: string; href?: string } | null;
      if (!data || data.type !== "vetchka:location" || typeof data.href !== "string") return;
      mirrorLocation(data.href);
    };
    window.addEventListener("message", onReady);
    window.addEventListener("message", onLocation);
    mountEditor();
    return () => {
      window.removeEventListener("message", onReady);
      window.removeEventListener("message", onLocation);
    };
  });

  // Адрес песочницы. В проде — uc.vetchka.ru, в dev — uc.localhost:<port>.

  /**
   * Адрес превью. Всегда настоящий URL на UC-origin: у документа есть
   * location, работают относительные пути и History API. Для ещё не
   * сохранённого паста — /draft/preview.html (содержимое всё равно придёт
   * через postMessage).
   */
</script>

<div class="shell" data-layout={layout}>
  <header class="toolbar">
    <div class="left">
      <a href="/" class="brand">vetchka</a>
      <input
        type="text"
        bind:value={title}
        placeholder={titlePlaceholder}
        oninput={() => {
          dirty = true;
          scheduleAutosave();
        }}
      />
      {#if isOwner}
        <span class="segment">
          <button class:active={mode === "ai"} onclick={() => setMode("ai")} title="чат с llm">
            <span class="ai-label">ai</span>
          </button>
          <button class:active={mode === "code"} onclick={() => setMode("code")} title="код">code</button>
        </span>
      {/if}
    </div>
    <div class="right">
      {#if !isOwner}<span class="muted">read-only</span>{/if}
      {#if status}<span class="muted">{status}</span>{/if}
      {#if error}<span class="error">{error}</span>{/if}

      <span class="segment">
        <button class:active={layout === "rows"} onclick={() => setLayout("rows")}>rows</button>
        <button class:active={layout === "columns"} onclick={() => setLayout("columns")}>columns</button>
        <button class:active={layout === "tree"} onclick={() => setLayout("tree")}>tree</button>
      </span>

      {#if currentSlug}
        <button onclick={fork} disabled={forkBusy} title="создать копию паста">
          {forkBusy ? (forkProgress ? `pow ${forkProgress.attempts.toLocaleString()}` : "forking…") : "fork"}
        </button>
      {/if}
      {#if currentSlug && isOwner}
        <button class:active={historyOpen} onclick={toggleHistory}>history</button>
      {/if}
      {#if isOwner}
        <button class="primary" onclick={() => save(false)} disabled={saving}>save</button>
      {/if}
    </div>
  </header>

  {#if dragKind !== null}
    <div class="drag-shield" style={`cursor: ${dragCursor}`}></div>
  {/if}

  <div class="main">
    {#if layout === "tree" && mode === "code"}
      <aside class="files">
        <div class="panel-header">
          <span class="muted">files</span>
          <button onclick={addFile} title="add file">+</button>
        </div>
        <ul>
          {#each files as f (f.path)}
            <li class:active={f.path === activePath}>
              <button class="file-name" onclick={() => selectFile(f.path)}>{f.path}</button>
              {#if f.path !== META_PATH}
                <span class="file-actions">
                  <button onclick={() => renameFile(f.path)} title="rename {f.path}">rename</button>
                  <button onclick={() => removeFile(f.path)} title="delete {f.path}">-</button>
                </span>
              {/if}
            </li>
          {/each}
        </ul>
      </aside>
    {/if}

    <div class="panes" bind:this={panesEl}>
      {#if mode === "ai" && isOwner}
        <section class="left-pane ai-pane" style={`flex: 0 0 ${splitPercent}%`}>
          {#if currentSlug}
            <ChatPanel
              slug={currentSlug}
              {title}
              {files}
              {activePath}
              executeTool={executeTool}
              onClose={() => setMode("code")}
            />
          {:else}
            <div class="ai-empty">
              <p class="muted">не удалось создать паст для чата</p>
              <button onclick={() => save(false)} disabled={saving}>retry</button>
            </div>
          {/if}
        </section>
      {:else}
        <section class="left-pane editor-pane" style={`flex: 0 0 ${splitPercent}%`}>
          <div class="tabbar">
            {#if layout === "tree"}
              <span class="tab active">{activePath}{dirty ? " •" : ""}</span>
            {:else}
              {#each files as f (f.path)}
                <button class="tab" class:active={f.path === activePath} onclick={() => selectFile(f.path)}>
                  {f.path}{f.path === activePath && dirty ? " •" : ""}
                </button>
              {/each}
            {/if}
          </div>
          <div class="editor-host" bind:this={editorHost}></div>
        </section>
      {/if}

      <div
        class="resizer"
        class:dragging={dragKind === "main"}
        role="separator"
        aria-orientation={layout === "rows" ? "horizontal" : "vertical"}
        onpointerdown={(e) => beginResize("main", e)}
      ></div>

      <section class="preview-pane">
        <div class="tabbar">
          <span class="tab">preview</span>
          <input
            class="query-input"
            bind:value={queryRaw}
            class:expanded={queryFocused || query !== ""}
            spellcheck="false"
            aria-label="get-параметры превью"
            title="get-параметры для превью и демки, можно вставить ссылку целиком"
            oninput={onQueryInput}
            onchange={commitQuery}
            onfocus={() => (queryFocused = true)}
            onblur={() => {
              queryFocused = false;
              commitQuery();
            }}
          />
          <button class="tab-action" onclick={openDemo} title="открыть превью в отдельном окне (demo)">
            demo ↗
          </button>
        </div>
        <iframe
          bind:this={previewFrame}
          title="preview"
          src={`${ucOrigin}/sandbox`}
          onload={() => void flushPreview()}
        ></iframe>
      </section>
    </div>

    {#if diff}
  <DiffView
    title={diff.title}
    beforeLabel={diff.label}
    before={diff.before}
    after={files}
    onClose={() => (diff = null)}
  />
{/if}

{#if historyOpen && currentSlug}
      <aside class="history">
        <div class="panel-header">
          <span class="muted">versions</span>
          <span class="panel-actions">
            <button onclick={snapshotNow} disabled={versionBusy} title="зафиксировать текущее состояние как версию">
              snapshot
            </button>
            <button onclick={toggleHistory} title="close history">×</button>
          </span>
        </div>
        {#if versionsLoading}
          <div class="panel-empty muted">loading…</div>
        {:else if versions.length === 0}
          <div class="panel-empty muted">no versions yet. use snapshot.</div>
        {:else}
          <ul>
            {#each versions as v (v.id)}
              <li>
                <div class="v-meta">
                  <span class="v-date">{formatDate(v.createdAt)}</span>
                  <span class="v-files muted">{fileCountLabel(v.fileCount)}</span>
                </div>
                <span class="v-actions">
                  <button onclick={() => showDiff(v)}>diff</button>
                  <button onclick={() => restore(v)} disabled={versionBusy}>restore</button>
                </span>
              </li>
            {/each}
          </ul>
        {/if}
      </aside>
    {/if}
  </div>
</div>

<style>
  .shell { display: flex; flex-direction: column; height: 100%; }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 6px 10px;
    background: var(--bg-elev);
    border-bottom: 1px solid var(--border);
    min-height: 34px;
  }
  .toolbar .left, .toolbar .right { display: flex; gap: 8px; align-items: center; }
  .brand { font-weight: 600; }

  /* Слово "ai" — единственный цветовой акцент, шрифт и размер не меняем. */
  .ai-label {
    background-image: linear-gradient(90deg, #4ec9b0, #7aa2f7 55%, #bb9af7);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
  }
  .toolbar input[type="text"] { width: 220px; }

  /* Переключатель layout'ов: три взаимоисключающих режима. */
  .segment { display: inline-flex; }
  .segment button { border-radius: 0; margin-left: -1px; }
  .segment button:first-child { border-radius: 3px 0 0 3px; margin-left: 0; }
  .segment button:last-child { border-radius: 0 3px 3px 0; }

  .main { flex: 1; display: flex; overflow: hidden; }

  .panes { flex: 1; display: flex; overflow: hidden; min-width: 0; min-height: 0; }
  .shell[data-layout="rows"] .panes { flex-direction: column; }
  .shell[data-layout="columns"] .panes,
  .shell[data-layout="tree"] .panes { flex-direction: row; }

  /* Ресайзер: тонкая полоса между панелями. */
  .drag-shield {
    position: fixed;
    inset: 0;
    z-index: 60;
  }

  .resizer {
    background: var(--border);
    flex: 0 0 auto;
    transition: background 0.12s ease;
  }
  .resizer:hover, .resizer.dragging { background: var(--accent); }
  .shell[data-layout="rows"] .resizer { height: 4px; cursor: row-resize; }
  .shell[data-layout="columns"] .resizer,
  .shell[data-layout="tree"] .resizer { width: 4px; cursor: col-resize; }

  .files {
    width: 190px;
    background: var(--bg-elev);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    flex-shrink: 0;
  }
  .files ul, .history ul { list-style: none; margin: 0; padding: 4px 0; }
  .files li { position: relative; display: flex; align-items: center; }
  .files li.active { background: var(--bg); }
  .file-name {
    flex: 1; text-align: left; background: transparent; border: 0;
    padding: 3px 10px; color: var(--fg);
    text-transform: none;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .files li.active .file-name { color: var(--accent); }

  /*
   * Действия лежат поверх строки, а не в потоке: имя файла занимает всю
   * ширину, а кнопки появляются справа при наведении.
   */
  .file-actions {
    position: absolute;
    right: 2px;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    gap: 2px;
    padding-left: 22px;
    background: linear-gradient(to right, transparent, var(--bg-elev) 22px);
    opacity: 0;
    pointer-events: none;
  }
  .files li:hover .file-actions,
  .files li.active:hover .file-actions {
    opacity: 1;
    pointer-events: auto;
  }
  .files li.active .file-actions {
    background: linear-gradient(to right, transparent, var(--bg) 22px);
  }
  .file-actions button { padding: 1px 5px; }

  .panel-header {
    display: flex; justify-content: space-between; align-items: center;
    gap: 8px; padding: 4px 10px;
    border-bottom: 1px solid var(--border);
    min-height: 28px;
    flex-shrink: 0;
  }
  .panel-header button { padding: 1px 6px; }
  .panel-actions { display: flex; gap: 4px; }
  .panel-empty { padding: 14px 10px; text-align: center; }

  .editor-pane, .preview-pane {
    display: flex; flex-direction: column; overflow: hidden; min-width: 0; min-height: 0;
  }
  .preview-pane { flex: 1; }
  .left-pane { display: flex; flex-direction: column; overflow: hidden; min-width: 0; min-height: 0; }
  .ai-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 16px;
    text-align: center;
    background: var(--bg-elev);
  }
  .ai-empty p { margin: 0; }

  .tabbar {
    min-height: 26px; background: var(--bg-elev); border-bottom: 1px solid var(--border);
    display: flex; align-items: stretch; overflow-x: auto; flex-shrink: 0;
  }
  .tab {
    padding: 0 10px; display: flex; align-items: center;
    background: transparent; border: 0; color: var(--fg-dim);
    text-transform: none;
    white-space: nowrap;
  }
  .tab.active { background: var(--bg); color: var(--fg); }
  .query-input {
    /* Пока поле пустое, оно почти не занимает места и не подсказывает о себе. */
    margin-left: auto;
    align-self: center;
    flex: 0 0 24px;
    width: 24px;
    min-width: 0;
    padding: 1px 4px;
    font: inherit;
    font-size: 11px;
    background: transparent;
    color: var(--fg);
    border: 1px solid transparent;
    border-radius: 3px;
    text-overflow: ellipsis;
    transition: flex-basis 0.15s ease, border-color 0.15s ease;
  }
  .query-input:hover { border-color: var(--border); }
  /* Разворачивается до заголовка preview, когда с полем работают. */
  .query-input.expanded {
    flex: 1 1 auto;
    margin-left: 10px;
    border-color: var(--border);
    outline: none;
  }
  .tab-action {
    margin-left: 0;
    background: transparent;
    border: 0;
    color: var(--fg-dim);
    padding: 0 10px;
  }
  .tab-action:hover { background: transparent; color: var(--accent); }
  .editor-host { flex: 1; overflow: hidden; min-height: 0; }

  .preview-pane iframe { flex: 1; width: 100%; border: 0; background: #fff; min-height: 0; }

  .history {
    width: 240px;
    background: var(--bg-elev);
    border-left: 1px solid var(--border);
    display: flex; flex-direction: column; overflow-y: auto; flex-shrink: 0;
  }
  .history li {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 5px 10px; border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }
  .v-actions { display: flex; gap: 4px; flex-shrink: 0; }
  .v-meta { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .v-date { color: var(--fg); }

  @media (max-width: 720px) {
    .shell[data-layout="columns"] .panes { flex-direction: column; }
    .files { display: none; }
  }
</style>